import { useId, useState, useRef, useImperativeHandle, type Ref, type ReactNode } from "react";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EditPasscodeField } from "./EditPasscodeField";
import {
  editPasscodeSchema,
  hasRoomChanges,
  toCreateInput,
  toEditInput,
  type RoomValues,
} from "./room-form-values";
import { ParticipantLimitSlider } from "./ParticipantLimitSlider";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldError,
} from "../ui/field";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { getRoomCode, useCreateRoom, useEditRoom, type Room } from "../../lib/rooms";
import {
  ROOM_NAME_MAX_LENGTH,
  ROOM_PASSWORD_MAX_LENGTH,
  createRoomSchema,
  editRoomSchema,
} from "../../../shared/room-schema.ts";
import { useAppStatus } from "../../lib/app-status";
import { activeCall } from "../../room/active-call";
import { RoomRequestError } from "../../lib/room-api";
import { MAX_ROOM_PARTICIPANTS } from "../../../shared/room-limits.ts";
import { cn } from "../../lib/utils";
import {
  RoomFieldError,
  roomDialogClass,
  roomDialogDescriptionClass,
  roomDialogTitleClass,
  roomErrorMessage,
  roomFieldErrors,
  roomFormClass,
  roomFormErrorClass,
  roomInputClass,
} from "./room-form";

// Labels sit beside their controls, as in the media settings popover, and
// line up with the first row of the control.
const rowLabel = "@sm/field-group:mt-2";

// Creates a room, or edits `room` when one is given. Pass a trigger as
// children to open the dialog from it.
export function RoomDialog({
  room,
  open,
  onOpenChange,
  children,
}: {
  room?: Room;
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  children?: ReactNode;
}) {
  const status = useAppStatus();
  const navigate = useNavigate();
  const create = useCreateRoom();
  const edit = useEditRoom(room?.id ?? "");
  const mutation = room ? edit : create;
  // Stay busy after creating while the app navigates to the new room.
  const busy = mutation.isPending || create.isSuccess;
  const setOpen = (value: boolean) => {
    if (busy) return;
    if (!value) mutation.reset();
    onOpenChange(value);
  };
  return (
    <RoomFormDialog
      room={room}
      defaultParticipantLimit={status.data?.defaultParticipantLimit}
      maxParticipantLimit={status.data?.maxParticipantLimit}
      open={open}
      onOpenChange={setOpen}
      loadCode={room ? (signal) => getRoomCode(room.id, signal) : undefined}
      busy={busy}
      error={mutation.error}
      onSubmit={async (values) => {
        if (room) {
          await edit.mutateAsync(toEditInput(values));
          edit.reset();
          onOpenChange(false);
        } else {
          const created = await create.mutateAsync(toCreateInput(values));
          // A live call asks before switching rooms, and a blocked navigation never
          // settles; close first so staying lands on home rather than a busy form.
          if (activeCall.liveRoomId()) {
            create.reset();
            onOpenChange(false);
          }
          await navigate({ to: "/room/$roomId", params: { roomId: created.id } });
        }
      }}
    >
      {children}
    </RoomFormDialog>
  );
}

type RoomFormHandle = { requestClose: () => void };
type RoomFormProps = {
  defaultParticipantLimit?: number | null;
  maxParticipantLimit?: number;
  loadCode?: (_signal: AbortSignal) => Promise<string>;
  room?: Room;
  busy: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (_values: RoomValues) => Promise<void>;
  ref?: Ref<RoomFormHandle>;
};

// Shares every dismissal path with the form, including the title-bar close,
// outside interaction and Escape. Kept independent of mutations for stories.
export function RoomFormDialog({
  room,
  open,
  onOpenChange,
  children,
  ...props
}: Omit<RoomFormProps, "onCancel" | "ref"> & {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const form = useRef<RoomFormHandle>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (props.busy) return;
        if (next) onOpenChange(true);
        else form.current?.requestClose();
      }}
    >
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent
        className={cn(roomDialogClass, "w-[min(32.5rem,calc(100vw-2rem))] p-7 max-[30rem]:p-6")}
      >
        <DialogHeader className="text-left">
          <DialogTitle className={roomDialogTitleClass}>
            {t(room ? "home.editTitle" : "home.createTitle")}
          </DialogTitle>
          <DialogDescription className={roomDialogDescriptionClass}>
            {room ? t("home.editBody", { name: room.name }) : t("home.createDescription")}
          </DialogDescription>
        </DialogHeader>
        {/* Unmounting the content resets the form to the latest room. */}
        <RoomForm {...props} ref={form} room={room} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

// The inner form renders from props alone (no query provider or router), so
// stories can mount it directly.
export function RoomForm({
  room,
  busy,
  error,
  onCancel,
  onSubmit,
  loadCode,
  ref,
  defaultParticipantLimit = null,
  maxParticipantLimit = MAX_ROOM_PARTICIPANTS,
}: RoomFormProps) {
  const { t } = useTranslation();
  const id = useId();
  const [originalPassword, setOriginalPassword] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const formElement = useRef<HTMLFormElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [initialValues] = useState<RoomValues>(() => ({
    visibility: room?.visibility ?? "public",
    name: room?.name ?? "",
    password: "",
    passcodeAction: "keep",
    limit: room
      ? room.maxParticipants
      : (defaultParticipantLimit ??
        (maxParticipantLimit < MAX_ROOM_PARTICIPANTS ? maxParticipantLimit : null)),
  }));
  const serverLimitError = error?.message === "participant-limit";
  const grandfatheredLimit = Boolean(
    room && (room.maxParticipants ?? MAX_ROOM_PARTICIPANTS) > maxParticipantLimit,
  );
  const form = useForm({
    defaultValues: initialValues,
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: ({ value }) => {
        const errors = room
          ? roomFieldErrors(editRoomSchema, toEditInput(value))
          : roomFieldErrors(createRoomSchema, toCreateInput(value));
        const passcodeErrors = room ? roomFieldErrors(editPasscodeSchema, value) : undefined;
        return errors || passcodeErrors
          ? { fields: { ...errors?.fields, ...passcodeErrors?.fields } }
          : undefined;
      },
    },
    onSubmit: ({ value }) => onSubmit(value),
  });
  function requestClose() {
    if (busy) return;
    if (!hasRoomChanges(form.state.values, initialValues, Boolean(room))) {
      onCancel();
      return;
    }
    const focused = document.activeElement;
    returnFocus.current =
      focused instanceof HTMLElement && formElement.current?.contains(focused)
        ? focused
        : (formElement.current?.querySelector("input") ?? null);
    setConfirmOpen(true);
  }
  useImperativeHandle(ref, () => ({ requestClose }));
  return (
    <form
      ref={formElement}
      noValidate
      className={roomFormClass}
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) void form.handleSubmit().catch(() => {});
      }}
    >
      {/* Two groups — [name, limit] and [visibility, passcode] — as nested
          FieldGroups, with no divider line. Rows inside a group are spaced
          apart and the gap between the groups is larger still. */}
      <FieldGroup className="gap-10 [&>[data-slot=field-group]]:gap-6">
        <FieldGroup>
          <form.Field name="name">
            {(field) => (
              <Field
                orientation="responsive"
                data-invalid={!field.state.meta.isValid || undefined}
                className="min-w-0"
              >
                <FieldLabel htmlFor={`${id}-name`} className={rowLabel}>
                  {t("home.name")}
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${id}-name`}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={!field.state.meta.isValid || undefined}
                    maxLength={ROOM_NAME_MAX_LENGTH}
                    placeholder={t("home.namePlaceholder")}
                    autoComplete="off"
                    className={roomInputClass}
                  />
                  <RoomFieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            )}
          </form.Field>
          <form.Field name="limit">
            {(field) => (
              <Field
                orientation="responsive"
                data-invalid={!field.state.meta.isValid || undefined}
                className="min-w-0"
              >
                <FieldLabel id={`${id}-limit`} className={rowLabel}>
                  {t("home.limitShort")}
                </FieldLabel>
                <FieldContent>
                  <ParticipantLimitSlider
                    value={field.state.value}
                    max={maxParticipantLimit}
                    invalid={serverLimitError}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    labelId={`${id}-limit`}
                    describedBy={`${id}-limit-hint${serverLimitError ? ` ${id}-limit-error` : ""}`}
                  />
                  <FieldDescription id={`${id}-limit-hint`}>
                    {grandfatheredLimit && field.state.value === initialValues.limit
                      ? t("home.existingLimitHint", {
                          current: room?.maxParticipants ?? t("home.unlimited"),
                        })
                      : t(
                          maxParticipantLimit < MAX_ROOM_PARTICIPANTS
                            ? "home.limitMaxHint"
                            : "home.limitHint",
                          { max: maxParticipantLimit },
                        )}
                  </FieldDescription>
                  {serverLimitError ? (
                    <FieldError id={`${id}-limit-error`} role="alert">
                      {t("home.errors.participant-limit", { max: maxParticipantLimit })}
                    </FieldError>
                  ) : null}
                  <RoomFieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            )}
          </form.Field>
        </FieldGroup>
        <FieldGroup>
          <form.Field name="visibility">
            {(field) => (
              <Field orientation="responsive" className="min-w-0">
                <FieldLabel id={`${id}-visibility`} className={rowLabel}>
                  {t("home.visibility")}
                </FieldLabel>
                <FieldContent className="gap-2.5">
                  <ToggleGroup
                    type="single"
                    size="sm"
                    value={field.state.value}
                    onValueChange={(value) => {
                      if (value) field.handleChange(value as "public" | "private");
                    }}
                    className="w-full"
                    aria-labelledby={`${id}-visibility`}
                  >
                    <ToggleGroupItem value="public">{t("home.publicShort")}</ToggleGroupItem>
                    <ToggleGroupItem value="private">{t("home.privateShort")}</ToggleGroupItem>
                  </ToggleGroup>
                  <FieldDescription>
                    {t(
                      field.state.value === "private"
                        ? "home.visibilityHint"
                        : "home.publicVisibilityHint",
                    )}
                  </FieldDescription>
                  {room?.hasPassword && field.state.value === "public" ? (
                    <Alert>
                      <TriangleAlert />
                      <AlertTitle>{t("home.publicRemovesCodeTitle")}</AlertTitle>
                      <AlertDescription>{t("home.publicRemovesCodeBody")}</AlertDescription>
                    </Alert>
                  ) : null}
                </FieldContent>
              </Field>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => [state.values.visibility, state.values.passcodeAction] as const}
          >
            {([visibility, passcodeAction]) =>
              visibility === "private" ? (
                <form.Field name="password">
                  {(field) =>
                    room ? (
                      <EditPasscodeField
                        key={room.id}
                        hasPassword={room.hasPassword}
                        canCopyCode={room.canCopyCode}
                        codeAvailable={room.codeAvailable}
                        loadCode={loadCode}
                        originalPassword={originalPassword}
                        onOriginalChange={setOriginalPassword}
                        action={passcodeAction}
                        password={field.state.value}
                        errors={field.state.meta.errors}
                        onBlur={field.handleBlur}
                        onValueChange={(password, action) => {
                          form.setFieldValue("passcodeAction", action);
                          field.handleChange(password);
                        }}
                      />
                    ) : (
                      <Field
                        orientation="responsive"
                        data-invalid={!field.state.meta.isValid || undefined}
                        className="min-w-0"
                      >
                        <FieldLabel htmlFor={`${id}-password`} className={rowLabel}>
                          {t("home.password")}
                        </FieldLabel>
                        <FieldContent>
                          <Input
                            id={`${id}-password`}
                            name={field.name}
                            type="password"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(event) => field.handleChange(event.target.value)}
                            aria-invalid={!field.state.meta.isValid || undefined}
                            maxLength={ROOM_PASSWORD_MAX_LENGTH}
                            autoComplete="new-password"
                            aria-describedby={`${id}-password-hint`}
                            className={cn(roomInputClass, "w-48 max-w-full")}
                            placeholder={t("home.noCode")}
                          />
                          <FieldDescription id={`${id}-password-hint`}>
                            {t("home.codeOnceHint")}
                          </FieldDescription>
                          <RoomFieldError errors={field.state.meta.errors} />
                        </FieldContent>
                      </Field>
                    )
                  }
                </form.Field>
              ) : null
            }
          </form.Subscribe>
        </FieldGroup>
      </FieldGroup>
      {error && !serverLimitError ? (
        <p role="alert" className={roomFormErrorClass}>
          {error.message === "rooms-per-user-limit" &&
          error instanceof RoomRequestError &&
          error.limit
            ? t("home.errors.rooms-per-user-limit-known", { limit: error.limit })
            : roomErrorMessage(t, error.message)}
        </p>
      ) : null}
      <DialogFooter className="mt-1">
        <Button type="button" variant="ghost" disabled={busy} onClick={requestClose}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {room ? t(busy ? "home.saving" : "home.save") : t(busy ? "home.creating" : "home.create")}
          {room ? null : <ArrowUpRight size={16} />}
        </Button>
      </DialogFooter>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus.current?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t("home.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("home.discardBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("home.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" disabled={busy} onClick={onCancel}>
              {t("home.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
